/**
 * Oversight analytics formatting utilities
 * Pure functions for duration, time, percentage, and ID resolution
 */

/**
 * Format seconds into human-readable duration (e.g., "2h 4m")
 * Granularity: minutes and hours; seconds ignored
 * @param {number|null|undefined} seconds - Total seconds
 * @returns {string} Formatted duration or "—" if invalid
 * @example
 * formatDuration(7440) // "2h 4m"
 * formatDuration(240) // "4m"
 * formatDuration(0) // "0m"
 * formatDuration(null) // "—"
 */
export function formatDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return '—';

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  }
  return `${hours}h ${minutes}m`;
}

/**
 * Format seconds into brief time format with mixed granularity
 * Shows seconds for <1 minute, minutes+seconds for minutes, hours+minutes for hours
 * @param {number|null|undefined} seconds - Total seconds
 * @returns {string} Formatted time (e.g., "38s", "4m 12s", "1h 5m") or "—" if invalid
 * @example
 * formatSecondsBrief(38) // "38s"
 * formatSecondsBrief(252) // "4m 12s"
 * formatSecondsBrief(3900) // "1h 5m"
 * formatSecondsBrief(null) // "—"
 */
export function formatSecondsBrief(seconds) {
  if (seconds == null || isNaN(seconds)) return '—';

  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const secs = Math.round(seconds % 60);

  if (hours === 0) {
    // Show minutes and seconds, omit if seconds is 0
    if (secs === 0) {
      return `${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  }

  // Show hours and minutes, omit seconds
  return `${hours}h ${minutes}m`;
}

/**
 * Generate a date range for analytics queries, spanning exactly `days` days and
 * ending today. Returns UTC-date strings in YYYY-MM-DD format.
 *
 * Both ends are INCLUSIVE server-side, so the span is `days`, not `days + 1`.
 * This previously subtracted a full `days` from today, which made "Last 30
 * days" a 31-day range and every derived per-day rate ~3% low.
 *
 * UTC, not the viewer's local calendar: these strings select UTC rollup
 * buckets, and they are re-read as UTC downstream — `coachOverviewDerive`
 * parses them as `T00:00:00Z`, `sessionTracks` compares against
 * `now.toISOString()`. Built from local getters they disagreed with both by a
 * day whenever the viewer's date ran ahead of UTC (east of UTC, after local
 * midnight), shifting the window by one day at each edge.
 *
 * @param {number} days - Length of the range in days, inclusive of today.
 * @returns {Object} Object with date_from and date_to as YYYY-MM-DD strings
 * @example
 * presetRange(7) // { date_from: "2024-01-04", date_to: "2024-01-10" } — 7 days
 * presetRange(1) // { date_from: "2024-01-10", date_to: "2024-01-10" } — today only
 */
export function presetRange(days) {
  const today = new Date();
  // Get date_to as today's date in YYYY-MM-DD
  const year = today.getUTCFullYear();
  const month = String(today.getUTCMonth() + 1).padStart(2, '0');
  const date = String(today.getUTCDate()).padStart(2, '0');
  const date_to = `${year}-${month}-${date}`;

  // `days - 1` because date_to is today and the server treats both ends as
  // inclusive. Clamped so 0 and 1 both mean "today only" rather than running
  // the range into tomorrow.
  const back = Math.max(0, Number(days) - 1);
  const from = new Date(today);
  from.setUTCDate(from.getUTCDate() - back);
  const fromYear = from.getUTCFullYear();
  const fromMonth = String(from.getUTCMonth() + 1).padStart(2, '0');
  const fromDate = String(from.getUTCDate()).padStart(2, '0');
  const date_from = `${fromYear}-${fromMonth}-${fromDate}`;

  return { date_from, date_to };
}

/**
 * Resolve canonical coach/user ID from object
 * Applies precedence: resolved_coach_user_id > resolved_coach_id > resolved_user_id > coach_user_id > coach_id > user_id
 * @param {Object|null|undefined} obj - Object with potential ID fields
 * @returns {string|null} First non-null/empty ID value or null
 * @example
 * resolvedId({ coach_id: '123', user_id: '456' }) // "123"
 * resolvedId({ resolved_coach_user_id: 'abc', coach_id: '123' }) // "abc"
 * resolvedId(null) // null
 * resolvedId({}) // null
 */
export function resolvedId(obj) {
  if (!obj || typeof obj !== 'object') return null;

  const precedence = [
    'resolved_coach_user_id',
    'resolved_coach_id',
    'resolved_user_id',
    'coach_user_id',
    'coach_id',
    'user_id',
  ];

  for (const key of precedence) {
    const value = obj[key];
    if (value != null && value !== '') {
      return value;
    }
  }

  return null;
}

/**
 * Format ISO timestamp as relative time
 * Returns relative strings for recent times, fallback to locale date string
 * @param {string|null|undefined} iso - ISO 8601 timestamp (e.g., "2024-01-10T14:30:00Z")
 * @returns {string} Relative time (e.g., "just now", "5m ago") or locale date string or "—"
 * @example
 * relativeTime("2024-01-10T14:35:00Z") // "just now" (if current time is 14:35:30)
 * relativeTime("2024-01-10T14:30:00Z") // "5m ago" (if current time is 14:35:00)
 * relativeTime("2024-01-10T10:00:00Z") // "4h ago" (if current time is 14:00:00)
 * relativeTime("2024-01-08T10:00:00Z") // "2d ago" (if current time is Jan 10)
 * relativeTime(null) // "—"
 */
export function relativeTime(iso) {
  if (!iso) return '—';

  try {
    const timestamp = new Date(iso);
    if (isNaN(timestamp.getTime())) return '—';

    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) {
      return 'just now';
    }
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    if (diffDays < 365) {
      return `${diffDays}d ago`;
    }

    // Fallback to locale date string for older timestamps
    return timestamp.toLocaleDateString();
  } catch {
    return '—';
  }
}

/**
 * Format decimal (0-1) as percentage
 * Rounds to nearest integer
 * @param {number|null|undefined} x - Decimal value between 0 and 1
 * @returns {string} Formatted percentage (e.g., "93%") or "—" if invalid
 * @example
 * pct(0.931) // "93%"
 * pct(1.0) // "100%"
 * pct(0) // "0%"
 * pct(null) // "—"
 */
export function pct(x) {
  if (x == null || isNaN(x)) return '—';

  const rounded = Math.round(x * 100);
  return `${rounded}%`;
}
